// Duplicate detection for the "deposits" collection.
//
// The same payment can reach deposits from four places: the Deposits page,
// Job Estimate Spreadsheet (single checks and split checks), Bank Statement
// Entries, and bulk upload. These rules decide when two entries look like the
// same money entered twice.
//
// Two entries are a possible duplicate when they are for the same property and
// the same amount AND either:
//   A. they carry the same real check number, or
//   B. they are for the same work order, their check numbers don't conflict,
//      and their dates are within 45 days of each other.
//
// Never matched: $0 "invoice only" rows, and rows that belong to the same split
// check (those share a check number on purpose).

const DAY_MS = 86400000;
const WO_MATCH_WINDOW_DAYS = 45;

const norm = (v) => String(v ?? "").trim().toLowerCase().replace(/\s+/g, " ");
const cents = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : Math.round(n * 100);
};

export const isBankStatementDeposit = (d) =>
  d?.sourceCollection === "bankStatementEntries" || d?.source === "bankStatementEntries";

/** Bank statement deposits store the bank name in checkNumber — not a real check #. */
const realCheckNumber = (d) => (isBankStatementDeposit(d) ? "" : norm(d.checkNumber));

const isInvoiceOnly = (d) => d?.splitInvoiceOnly || d?.invoiceOnly || cents(d?.amount) === 0;

const dateMs = (d) => {
  const t = Date.parse(d?.date || "");
  return isNaN(t) ? null : t;
};

const sameSplitGroup = (a, b) => a.checkGroupId && a.checkGroupId === b.checkGroupId;

const checkNumbersCompatible = (a, b) => {
  if (isBankStatementDeposit(a) || isBankStatementDeposit(b)) return true;
  const ca = realCheckNumber(a);
  const cb = realCheckNumber(b);
  return !ca || !cb || ca === cb;
};

/** True when a and b look like the same payment. Returns the reason, or "". */
export const duplicateReason = (a, b) => {
  if (!a || !b || a.id === b.id) return "";
  if (isInvoiceOnly(a) || isInvoiceOnly(b)) return "";
  if (sameSplitGroup(a, b)) return "";
  if (norm(a.property) !== norm(b.property)) return "";
  if (cents(a.amount) !== cents(b.amount)) return "";

  const ca = realCheckNumber(a);
  if (ca && ca === realCheckNumber(b)) return "Same property, amount and check #";

  const wa = norm(a.invoiceNumber);
  if (wa && wa === norm(b.invoiceNumber) && checkNumbersCompatible(a, b)) {
    const ta = dateMs(a);
    const tb = dateMs(b);
    if (ta === null || tb === null || Math.abs(ta - tb) <= WO_MATCH_WINDOW_DAYS * DAY_MS) {
      return "Same property, amount and work order";
    }
  }
  return "";
};

/** Entries already saved that match a new, unsaved entry. */
export const findMatchesForNew = (candidate, deposits) =>
  deposits.filter((d) => d.id !== candidate.id && duplicateReason({ ...candidate, id: "__new__" }, d));

/** Stable signature for a group; used to remember "not a duplicate" answers. */
export const groupSignature = (ids) => [...ids].sort().join("|");

/**
 * Which entry to keep by default:
 *  - a bank statement deposit (its money is already in the ledger through
 *    Bank Statement Entries; deleting it and keeping a manual copy would leave
 *    the payment counted twice),
 *  - then part of a split check (deleting one piece breaks the split),
 *  - then linked to a work order, then already mirrored to transactions,
 *  - then the one entered first.
 */
const keepScore = (d) =>
  (isBankStatementDeposit(d) ? 8 : 0) +
  (d.splitCheck ? 4 : 0) +
  (d.invoiceNumber ? 2 : 0) +
  (d.txId ? 1 : 0);

const createdMs = (d) => {
  const c = d.createdAt;
  if (!c) return Infinity;
  if (typeof c.toMillis === "function") return c.toMillis();
  if (typeof c.seconds === "number") return c.seconds * 1000;
  const t = Date.parse(c);
  return isNaN(t) ? Infinity : t;
};

export const recommendKeep = (items) =>
  [...items].sort((a, b) => keepScore(b) - keepScore(a) || createdMs(a) - createdMs(b))[0];

/**
 * Cluster every deposit into duplicate groups (union-find, so A~B and B~C
 * end up together). Groups the user marked "not a duplicate" are left out as
 * long as nobody new has joined them.
 */
export const findDuplicateGroups = (deposits) => {
  const list = deposits.filter((d) => d && d.id && !isInvoiceOnly(d));
  const parent = new Map(list.map((d) => [d.id, d.id]));
  const reasons = new Map();
  const find = (x) => {
    while (parent.get(x) !== x) {
      parent.set(x, parent.get(parent.get(x)));
      x = parent.get(x);
    }
    return x;
  };

  // Only compare entries that share property + amount.
  const buckets = new Map();
  list.forEach((d) => {
    const key = `${norm(d.property)}|${cents(d.amount)}`;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(d);
  });

  buckets.forEach((bucket) => {
    for (let i = 0; i < bucket.length; i++) {
      for (let j = i + 1; j < bucket.length; j++) {
        const reason = duplicateReason(bucket[i], bucket[j]);
        if (!reason) continue;
        const ra = find(bucket[i].id);
        const rb = find(bucket[j].id);
        if (ra !== rb) parent.set(ra, rb);
        reasons.set(bucket[i].id, reason);
        reasons.set(bucket[j].id, reason);
      }
    }
  });

  const byRoot = new Map();
  list.forEach((d) => {
    if (!reasons.has(d.id)) return;
    const r = find(d.id);
    if (!byRoot.has(r)) byRoot.set(r, []);
    byRoot.get(r).push(d);
  });

  const groups = [];
  byRoot.forEach((items) => {
    if (items.length < 2) return;
    const signature = groupSignature(items.map((d) => d.id));
    if (items.every((d) => d.duplicateDismissedSig === signature)) return;
    const sorted = [...items].sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
    groups.push({
      key: signature,
      signature,
      items: sorted,
      reason: reasons.get(items[0].id),
      keepId: recommendKeep(items).id,
    });
  });

  return groups.sort((a, b) => String(b.items[0].date || "").localeCompare(String(a.items[0].date || "")));
};

/** Plain-English label for where an entry came from. */
export const depositSourceLabel = (d) => {
  if (isBankStatementDeposit(d)) return "Bank Statement";
  if (d?.createdFrom === "JobEstimateSpreadsheetSplitCheck") return "Spreadsheet (split)";
  if (d?.createdFrom === "JobEstimateSpreadsheet") return "Spreadsheet";
  if (d?.createdFrom === "DepositsBulkUpload") return "Bulk Upload";
  return "Deposits";
};
